/** UI copy for the dashboard / My Desk. Shared labels (Cancel, Save…) live in `COMMON_TEXT`. */
export const DASHBOARD_TEXT = {
  columns: {
    code: 'Client Code',
    client: 'Client Name',
    service: 'Service',
    due: 'Due Date',
    month: 'Period',
    filingDate: 'Filing Date',
    basis: 'Basis Period',
    step: 'Current step',
    with: 'With whom',
    assignedBy: 'Assigned by',
  },
  columnPicker: {
    unchanged: 'Columns unchanged',
    deskHeader: 'Columns on my desk',
    boardHeader: 'Columns on the dashboard',
    shown: (count: number, total: number) => `${count} of ${total} shown`,
    keepOne: 'Keep at least one column',
    saved: (count: number) => `${count} column(s) shown`,
    hint: 'Tick what you want on the table. Remembered until you change it.',
  },
  firmTiles: {
    all: { label: 'All tasks', sub: 'in this view' },
    unassigned: { label: 'Unassigned Tasks', sub: 'nothing started yet' },
    wip: { label: 'Work in progress', sub: 'under way with the team' },
    completed: { label: 'Completed tasks', sub: 'everything finished' },
  },
  deskTiles: {
    all: { label: 'All allocated tasks', sub: 'now + completed' },
    now: { label: 'With me now', sub: 'waiting on you today' },
    done: { label: 'Completed by me', sub: 'your part is finished' },
    archived: { label: 'Archived', sub: 'moved to the archive' },
  },
  stepBand: {
    ariaLabel: 'Pending workflow steps',
    pending: 'Pending',
    workflowSteps: 'workflow steps',
  },
  stepMeter: {
    completed: 'Completed',
  },
  taskDrawer: {
    fallbackTitle: 'Task',
    onHold: 'On hold',
    sentBack: (count: number) => `Sent back ${count}×`,
    calendarYear: 'Calendar year',
    currentStep: 'Current step',
    closed: 'Closed',
    with: 'With',
    nobodyYet: 'Nobody yet',
    allocatedTime: 'Allocated Time',
    completedTime: 'Completed Time',
    allocatedBy: 'Default Allocation To',
    queries: 'Queries',
    filingReference: 'Filing & reference data',
    federalAck: 'Federal return ack. no.',
    stateAckDash: (key: string) => `State return ack. no. — ${key}`,
    stateAckParen: (key: string) => `State return ack. no. (${key})`,
    confirmationNo: (key: string) => `${key} confirmation / filing no.`,
    federalAckPlaceholder: 'Acknowledgement from the IRS',
    noStateReturns: "No state returns on this client's profile.",
    detailsAriaLabel: 'Task details',
    notesTab: 'Notes & attachments',
    workflowTab: 'Workflow',
    ownerAssigned: (code: string, service: string, owner: string | undefined) =>
      `${code} — ${service} assigned to ${owner ?? 'nobody'}`,
  },
  actions: {
    sendBackTarget: (num: number, label: string, holder: string) => `${num}. ${label} — ${holder}`,
    theClient: 'the client',
    nobody: 'nobody',
    sendBackTo: 'Send back to',
    sendBack: 'Send back',
    completedOn: (date: string) => `Work completed on ${date}. Filed copy saved to the client folder.`,
    completed: 'Work completed. Filed copy saved to the client folder.',
    notStarted: 'Not started yet.',
    withSomeoneElse: (name: string | undefined) => `This task is with ${name ?? 'someone else'}.`,
    notYetAssigned: (service: string) =>
      `Not yet assigned — set this client's Assigned to on its ${service} profile before this can move forward.`,
    markCompleted: 'Mark as completed',
    saveRecordsReceived: 'Save — records received',
    saveAndComplete: 'Save & complete',
    saveAndPassOn: 'Save & pass on',
    completeWork: 'Complete work',
    passOnTo: 'Pass on to',
    nextStep: 'Next step',
    allocatedTo: 'Allocated to',
    remarkPlaceholder: 'Anything they need to know',
    onQueryHold: 'On query hold — see the Queries tab below',
    resume: 'Resume',
    putOnHold: 'Put on hold',
  },
  history: {
    empty: 'No notes or attachments yet.',
    system: 'System',
  },
  workflow: {
    filedHere: '★ filed here',
    withClient: 'With the client',
    recordsReceived: (date: string) => `records received ${date}`,
    recordsAwaited: 'records awaited',
    unassigned: 'unassigned',
    allocated: 'allocated',
    completed: 'completed',
  },
  team: {
    nobody: 'Nobody',
    saved: 'Default team saved.',
  },
  page: {
    noAccounts: 'No accounts',
    unassigned: 'Unassigned',
    chasingClient: 'chasing client',
    queryOpen: 'Query open',
    archive: 'Archive',
    unarchive: 'Unarchive',
    noGroup: '(No group)',
    emptyYear: (year: number) =>
      `No work in ${year} yet. Open Client Master and use Rollover to bring clients into this year.`,
    emptyDeskNow: 'Nothing is sitting with you right now.',
    emptyDesk: 'Nothing in your book matches this.',
    emptyFirm: 'Nothing matches these filters.',
    deskFooter: (shown: number, verb: string, name: string | undefined, service: string | undefined) =>
      `${shown} ${verb} ${name ?? 'you'} ${service ? `in ${service}` : 'across all services'}`,
    firmFooter: (shown: number, total: number, service: string | undefined) =>
      `${shown} of ${total} ${service ? `${service} ` : ''}obligations shown`,
    exportSaved: (fileName: string) => `Saved ${fileName}`,
    adminTitle: 'Dashboard',
    deskTitle: 'My Desk',
    defaultTeam: 'Default team',
    generated: (created: number, redated: number) => `${created} new task(s) · ${redated} re-dated`,
    refreshTasks: 'Refresh tasks',
    refreshTasksHint: 'CTRL / CMD + R',
    searchPlaceholder: 'Search code, client or form',
    searchAriaLabel: 'Search tasks',
    groupPlaceholder: 'Client Groups',
    entityPlaceholder: 'Entity Types',
    servicePlaceholder: 'All Services',
    scopeAriaLabel: 'Scope',
    firmWide: 'Firm-wide',
    myWork: 'My work',
    excel: 'Excel (.xlsx)',
    csv: 'CSV (.csv)',
    tableAriaLabel: 'Tasks',
    teamHint:
      "New Income Tax Return tasks start with these people in each role. A client's own team, when set in Client Master, takes precedence.",
  },
} as const;
